import { useMutation } from "@tanstack/react-query";
import { Card, Form, Input, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createGithubConnection,
  fetchGithubBranches,
  fetchGithubFilesList,
  updateGithubConnection,
} from "../../../api/apiCallManager"; // Ensure these paths are correct
import useGithubConnectionFormData from "../../../components/hooks/useGithubConnectionFormData";
import AccessTo from "../../../components/is-show/AccessTo";
import FormLabel from "../../../components/label/FormLabel";
import { showError, showSuccess } from "../../../helpers/alerts";
import { isFederatedLearning } from "../../../helpers/authFunctions";
import { FL } from "../../router/AddRoutesJson";

const { Option } = Select;

const ConfigureGithubForModelTraining = () => {
  const [form] = Form.useForm();
  const [branchList, setBranchList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState(""); // State for file name
  const [isSubmit, setIsSubmit] = useState(false);
  const { id } = useParams();
  // Query to fetch initial form data
  const { formData, isFormDataLoaded } =
    useGithubConnectionFormData("Model_Quality");

  const createMutation = useMutation({
    mutationFn: createGithubConnection,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Github submitted successfully!");
        setIsSubmit(true);
      }
    },
  });
  const updateGithubConnectionMutation = useMutation({
    mutationFn: updateGithubConnection,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Github updated successfully!");
      }
    },
  });

  const fetchBranchesMutation = useMutation({
    mutationFn: fetchGithubBranches,
    onSuccess: (data) => {
      setBranchList(data.body || []);
    },
    onError: () => {
      showError("Failed to load branches");
    },
  });

  const fetchFilesMutation = useMutation({
    mutationFn: fetchGithubFilesList,
    onSuccess: (data) => {
      setFileList(data.body || []);
    },
    onError: () => {
      showError("Failed to load files");
    },
  });

  const onFinish = (values) => {
    // LOGGER({ ...values, fileName: selectedFileName });
    if (formData?.length !== 0) {
      updateGithubConnectionMutation.mutate({
        ...values,
        fileName: selectedFileName,
        record_id: formData[0].record_id,
        scriptType: "Model_Quality",
      });
    } else {
      createMutation.mutate({
        ...values,
        fileName: selectedFileName,
        scriptType: "Model_Quality",
      });
    }
  };

  const handleValuesChange = () => {
    const { repoUrl, accessToken } = form.getFieldsValue();
    if (branchList.length === 0 && repoUrl && accessToken) {
      fetchBranchesMutation.mutate({ repoUrl, accessToken });
    }
  };

  const handleBranchChange = (branchName) => {
    const { repoUrl, accessToken } = form.getFieldsValue();
    if (repoUrl && accessToken && branchName) {
      fetchFilesMutation.mutate({ repoUrl, accessToken, branchName });
    }
  };

  const handleFileChange = (filePath) => {
    const file = fileList?.find((f) => f.path === filePath);
    setSelectedFileName(file?.name || ""); // Set selected file name
  };
  useEffect(() => {
    if (id && isFormDataLoaded && formData.length !== 0) {
      const { repoUrl, accessToken, branchName } = formData[0];
      fetchBranchesMutation.mutate({ repoUrl, accessToken });
      fetchFilesMutation.mutate({ repoUrl, accessToken, branchName });
      form.setFieldsValue(formData[0]);
    }
  }, [formData]);
  const onlyEnableFL = !isFederatedLearning();
  return (
    <div className={`p-5  ${onlyEnableFL && "disableAll"}`}>
      {/* {JSON.stringify(formData)}=========== */}
      <Spin spinning={!formData}>
        <Card title="Configure Model Training Script" bordered={false}>
          <Form
            name="github_form"
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            form={form}
            autoComplete="false"
            disabled={onlyEnableFL}
          >
            <Form.Item
              name="repoUrl"
              label={<FormLabel> GitHub URL</FormLabel>}
              rules={[
                {
                  type: "url",
                  required: true,
                  message: "Please input the GitHub URL!",
                },
              ]}
            >
              <Input
                onChange={handleValuesChange}
                placeholder="Enter GitHub Repository URL"
              />
            </Form.Item>
            <Form.Item
              name="accessToken"
              label={<FormLabel> Access Token</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please input the Access Token!",
                },
              ]}
            >
              <Input
                onChange={handleValuesChange}
                placeholder="Enter GitHub Access Token"
              />
            </Form.Item>
            <Form.Item
              name="branchName"
              label={<FormLabel> Branch Name</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please select a branch!",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select a branch"
                loading={fetchBranchesMutation.isPending}
                notFoundContent={
                  fetchBranchesMutation.isPending
                    ? "Loading..."
                    : "No branches found"
                }
                disabled={
                  fetchBranchesMutation.isPending ||
                  branchList.length === 0 ||
                  onlyEnableFL
                }
                onChange={handleBranchChange}
              >
                {branchList.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="filePath"
              label={<FormLabel> File Name</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please select a file!",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select a file"
                loading={fetchFilesMutation.isPending}
                notFoundContent={
                  fetchFilesMutation.isPending ? "Loading..." : "No files found"
                }
                disabled={
                  fetchFilesMutation.isPending ||
                  fileList?.length === 0 ||
                  onlyEnableFL
                }
                onChange={handleFileChange} // Set selected file name
              >
                {fileList?.map((file) => (
                  <Option key={file.path} value={file.path}>
                    {file.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <AccessTo role={[FL]}>
              <div className="flex justify-center mt-3">
                <button
                  type="submit"
                  disabled={
                    isSubmit || updateGithubConnectionMutation.isPending
                  }
                  className="bg-[#0057B8] mt-1 py-2 px-4 rounded-sm text-[13px] w-[99%] text-white"
                >
                  {formData?.length == 0 ? "Submit" : "Update"}
                </button>
              </div>
            </AccessTo>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default ConfigureGithubForModelTraining;
